/** Bekannte Einheiten und ihre Kurzformen. */
export const UNIT_LABELS = {
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  l: 'l',
  tl: 'TL',
  el: 'EL',
  prise: 'Prise',
  priese: 'Prise',
  stueck: 'Stück',
  stk: 'Stück',
  st: 'Stück',
  bund: 'Bund',
  zehe: 'Zehe',
  scheibe: 'Scheibe',
  scheiben: 'Scheiben',
  dose: 'Dose',
  dosen: 'Dosen',
  packung: 'Päckchen',
  päckchen: 'Päckchen',
  becher: 'Becher',
  glas: 'Glas',
  flasche: 'Flasche',
  tasse: 'Tasse',
  handvoll: 'Handvoll',
} as const;

/** Einheitentyp aus der Tabelle oben. */
export type KnownUnit = keyof typeof UNIT_LABELS;

/** Volumen in Millilitern für einen Esslöffel und einen Teelöffel. */
export const MILLILITERS_PER_TABLESPOON = 15;
export const MILLILITERS_PER_TEASPOON = 5;

/**
 * Bringt eine geschriebene Einheit auf die bekannte Kurzform.
 * Unbekannte Einheiten bleiben unverändert, damit nichts verloren geht.
 */
export function normalizeUnit(unit: string): string {
  const trimmed = unit.trim();
  if (trimmed === '') {
    return '';
  }
  const key = trimmed.toLowerCase().replace(/\.$/, '');
  const known = UNIT_LABELS[key as KnownUnit];
  if (known !== undefined) {
    return known;
  }
  return trimmed;
}

/**
 * Rechnet eine Menge in eine kleinere Einheit um, damit beim Einkaufen
 * zusammengefasst werden kann. Kilo zu Gramm, Liter zu Milliliter.
 * Alle anderen Einheiten bleiben unverändert.
 */
export function toBaseUnit(amount: number, unit: string): { amount: number; unit: string } {
  const normalized = normalizeUnit(unit);
  switch (normalized.toLowerCase()) {
    case 'kg':
      return { amount: amount * 1000, unit: 'g' };
    case 'l':
      return { amount: amount * 1000, unit: 'ml' };
    default:
      return { amount, unit: normalized };
  }
}

/**
 * Formatiert eine Menge mit deutscher Zahlenschreibweise und Einheit.
 * Ganzzahlen bekommen kein Komma, Nachkommastellen werden kurz dargestellt.
 */
export function formatAmount(amount: number, unit?: string): string {
  const value = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(amount);
  const normalizedUnit = unit === undefined ? '' : normalizeUnit(unit);
  return normalizedUnit === '' ? value : `${value} ${normalizedUnit}`;
}
